import React from "react";
import { LIFT_LOG_MAX } from "./lib/outbox";
import { liftSectionsOf, liftTotals, liftInches, liftVariance } from "./lib/concrete.actuals";   // the on-site lift log (plan §5.3; roadmap §13 item 11)
import * as ReactDOM from "react-dom";
import { Icon, CompanyLogo } from "./registry";
import { computeQuoteView } from "./quote";
import { brandOf, verticalOf, jobTypesOf } from "./lib/verticals";
import { concreteSectionsFor, concreteBeforeOnsite, concreteReadinessFor } from "./lib/concrete.steps";
/* ============================================================
   Fence Works — the concrete crew sheet and Crew Mode's step list
   (docs/SERVICE_SWITCHER_PLAN.md §5.3; roadmap §13 item 3)

     • ConcreteCrewSheet   — the printable sheet for a concrete job: the
                             scope, "before onsite", one section per job type
                             in crew order (lib/concrete.steps.ts) with the
                             per-surface notes and photo prompts, the supply
                             list in load / order units, the time and cure.
                             crewsheet.tsx's CrewBuildSheet hands a concrete
                             quote here; the fence sheet is untouched.
     • ConcreteStepsCard   — the same steps on the crew's phone, ticked by the
                             on-site checklist (CONCRETE_CHECK_LIST)
     • ConcreteReadyChips  — the two things the crew must know before the
                             first hole / the first coat
   ============================================================ */

const fmtD = (s: any) => { if (!s) return ""; const d = new Date(s + "T00:00:00"); return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
const fmtQty = (n: any) => { const v = Number(n) || 0; return (Math.abs(v - Math.round(v)) < 0.05 ? Math.round(v) : Math.round(v * 10) / 10).toLocaleString("en-US"); };

/* the cure per job type off the priced blocks (engine.concrete PricedBlock.time) */
function cureByJobType(result: any) {
  const out: any = {};
  (((result && result.concrete && result.concrete.blocks) || []) as any[]).forEach((b) => { if (b && b.jobType && b.time && !out[b.jobType]) out[b.jobType] = b.time; });
  return out;
}
/* the quote behind a work order, and its job types */
function quoteOf(wo: any, store: any) {
  return wo && wo.quoteId ? (((store && store.quotes) || []) as any[]).find((x) => x && x.id === wo.quoteId) || null : null;
}

function ConcreteCrewSheet({ quote: q, store, settings, onClose }: any) {
  const sIn = settings || (store && store.settings) || {};
  const view = computeQuoteView(q, sIn);
  const result: any = view.quote;
  const blocks: any[] = (q.inputs && q.inputs.concrete && Array.isArray(q.inputs.concrete.blocks)) ? q.inputs.concrete.blocks : [];
  const wo = ((store && store.workOrders) || []).find((w: any) => w.quoteId === q.id);
  const crew = wo ? ((sIn.crews || []).find((c: any) => c.id === wo.crewId)) : null;
  const brand = brandOf(store && store.company ? store : { settings: sIn, company: (store && store.company) || {} }, "concrete");
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  const sections = concreteSectionsFor(blocks, cureByJobType(result));
  const before = concreteBeforeOnsite(blocks.map((b) => b.jobType));
  const supplies: any[] = result.mat || [];
  const equipment: any[] = (result.concrete && result.concrete.equipment) || [];
  const timeText = (result.concrete && result.concrete.timeText) || "";
  const sched = wo && wo.scheduledStart
    ? fmtD(wo.scheduledStart) + (wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? " – " + fmtD(wo.scheduledEnd) : "")
    : null;
  const r = (wo && wo.readiness) || {};
  const readiness = concreteReadinessFor(blocks.map((b) => b.jobType));
  const notes = q.inputs && q.inputs.concrete && q.inputs.concrete.notes;

  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="clipboard" size={14} className="muted" />Crew sheet</span>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll">
          <div className="crew-sheet">
            <div className="cs-head">
              <div className="row gap-12">
                <CompanyLogo company={brand} size={40} />
                <div>
                  <div className="display" style={{ fontSize: 19 }}>{brand.name || "Your Company"}</div>
                  <div className="cs-dim mono">{[brand.phone, brand.address].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="display" style={{ fontSize: 21, letterSpacing: ".04em" }}>CREW SHEET</div>
                <div className="mono" style={{ fontSize: 12, marginTop: 3 }}>Job #{String(q.id || "").slice(-6).toUpperCase()} · {new Date().toLocaleDateString()}</div>
              </div>
            </div>

            <div className="cs-job">
              <div>
                <div className="cs-kicker">Customer / Job site</div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{q.customer || "Customer"}</div>
                <div className="cs-dim">{q.address || "Address on work order"}</div>
                {q.phone && <div className="cs-dim mono">{q.phone}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="cs-kicker">Crew</div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{crew ? crew.name + (crew.foreman ? " · " + crew.foreman : "") : "Unassigned"}</div>
                <div className="cs-dim">{sched ? "Scheduled " + sched : "Not scheduled yet"}</div>
                {timeText && <div className="cs-dim mono" style={{ marginTop: 2 }}>{timeText}</div>}
              </div>
            </div>

            {/* the scope: what's being done, where, how much */}
            <div className="cs-kicker">Scope</div>
            <table className="cs-table">
              <thead><tr><th>Work</th><th>Where</th><th style={{ textAlign: "right" }}>Size</th></tr></thead>
              <tbody>
                {(((result.concrete && result.concrete.blocks) || []) as any[]).map((b) => (
                  <tr key={b.key}><td><b>{b.label}</b><div className="cs-dim">{b.sub}</div></td><td>{b.where || "—"}</td><td className="tnum" style={{ textAlign: "right", fontWeight: 700 }}>{Math.round(Number(b.size) || 0).toLocaleString("en-US")} {b.unit}</td></tr>
                ))}
              </tbody>
            </table>

            {/* readiness as the office left it, and the truck list */}
            <div className="cs-kicker">Before onsite <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· the night before, and loading the truck</span></div>
            <div className="cs-steps">
              {readiness.map((it) => (
                <div key={it.key} className="cs-step"><span className={"cs-cbx" + (r[it.key] ? " on" : "")} aria-hidden="true">{r[it.key] ? "✓" : ""}</span><div className="cs-step-body"><b>{it.label}</b> <span className="cs-dim">— {it.sub}{r[it.key] ? " · done" : ""}</span></div></div>
              ))}
              {before.map((t, i) => (
                <div key={"b" + i} className="cs-step"><span className="cs-cbx" aria-hidden="true" /><div className="cs-step-body">{t}</div></div>
              ))}
            </div>

            {sections.map((sec) => (
              <div key={sec.jobType} className="cs-section">
                <div className="cs-kicker">{sec.label} <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· {sec.scope} · work in this order · check off as you go</span></div>
                {sec.notes.length > 0 && <ul className="cs-notes">{sec.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
                <div className="cs-steps">
                  {sec.steps.map((st) => (
                    <div key={st.n} className="cs-step">
                      <span className="cs-cbx" aria-hidden="true" />
                      <span className="cs-stepnum mono">{String(st.n).padStart(2, "0")}</span>
                      <div className="cs-step-body">
                        <div className="cs-step-head">
                          <b>{st.title}</b>
                          {st.photo && <span className="cs-qty mono">photo · {st.photo}</span>}
                          <span className="cs-step-spec mono">{st.spec}</span>
                        </div>
                        {st.note && <div className="cs-step-note">{st.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="cs-kicker">Supplies <span className="cs-dim" style={{ textTransform: "none", letterSpacing: 0 }}>· load the truck — what the job consumes, and what to carry</span></div>
            <table className="cs-table cs-bom">
              <thead><tr><th style={{ width: 26 }}></th><th>Item</th><th style={{ textAlign: "right" }}>Job needs</th><th style={{ textAlign: "right" }}>Carry</th></tr></thead>
              <tbody>
                {supplies.map((m, k) => (
                  <tr key={m.key || k}>
                    <td><span className="cs-cbx sm" aria-hidden="true" /></td>
                    <td style={{ fontWeight: 600 }}>{m.label}{m.sku ? <span className="cs-dim"> · {m.sku}</span> : null}{m.supplier ? <span className="cs-dim"> · {m.supplier}</span> : null}</td>
                    <td className="tnum" style={{ textAlign: "right" }}>{fmtQty(m.qty)} {m.uom}</td>
                    <td className="tnum" style={{ textAlign: "right", fontWeight: 700 }}>{m.packLabel || `${Math.ceil(Number(m.qty) || 0)} ${m.uom}`}</td>
                  </tr>
                ))}
                {equipment.map((e, k) => (
                  <tr key={e.key || "e" + k}><td><span className="cs-cbx sm" aria-hidden="true" /></td><td style={{ fontWeight: 600 }}>{e.label} <span className="cs-dim">· equipment</span></td><td></td><td className="tnum" style={{ textAlign: "right" }}>{e.sub}</td></tr>
                ))}
                {!supplies.length && !equipment.length && <tr><td colSpan={4} className="cs-dim">No recipe on the rate card yet — nothing to load from here.</td></tr>}
              </tbody>
            </table>
            <p className="cs-dim" style={{ margin: "6px 0 0" }}>Quantities come from the rate card's recipe; the office's order may carry spare. Count the foam sets back in.</p>

            {notes && (
              <><div className="cs-kicker">Notes</div>
                <p style={{ margin: 0, fontSize: 12.5, whiteSpace: "pre-wrap" }}>{notes}</p></>
            )}

            <div className="cs-foot">
              <b>Photograph first — photograph last.</b> Before photos from the spots you'll shoot the after photos from; both land on the customer's hub. Check the slab for heat and utilities before the first hole. Keep cars off until the holes are patched and the sealer or coating has cured — and tell the customer the times.
              <div className="cs-sign">
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl">Foreman — cure times told to the customer</div></div>
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl">Date</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>, document.body);
}

/* Crew Mode: the same steps on the phone, in crew order, each ticked by the
   on-site checklist item it names (FieldNotes writes wo.fieldChecklist). */
function ConcreteStepsCard({ wo, store }: any) {
  const q = quoteOf(wo, store);
  const [open, setOpen] = React.useState<boolean>(() => wo && wo.status === "in_progress");
  if (!q || verticalOf(q) !== "concrete") return null;
  let result: any = null;
  try { result = computeQuoteView(q, (store && store.settings) || {}).quote; } catch (e) { return null; }
  const blocks: any[] = (q.inputs && q.inputs.concrete && Array.isArray(q.inputs.concrete.blocks)) ? q.inputs.concrete.blocks : [];
  const sections = concreteSectionsFor(blocks, cureByJobType(result));
  if (!sections.length) return null;
  const check = (wo && wo.fieldChecklist) || {};
  const total = sections.reduce((n, s) => n + s.steps.length, 0);
  return (
    <div className="crew-steps" style={{ marginBottom: 10 }}>
      <button type="button" className="row between" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ width: "100%", background: "none", border: 0, color: "inherit", padding: "6px 0", cursor: "pointer", font: "inherit" }}>
        <span className="kicker">Today's steps · {total}</span><Icon name={open ? "chevdown" : "chevright"} size={14} />
      </button>
      {open && sections.map((sec) => (
        <div key={sec.jobType} style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{sec.label} <span className="muted" style={{ fontWeight: 400 }}>· {sec.scope}</span></div>
          {sec.notes.map((n, i) => <div key={i} className="muted" style={{ fontSize: 12, margin: "2px 0" }}>⚠ {n}</div>)}
          {sec.steps.map((st) => {
            const done = !!(st.check && check[st.check] && check[st.check].done);
            return (
              <div key={st.n} className="row gap-8" style={{ alignItems: "flex-start", padding: "5px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
                <span className="mono muted" style={{ fontSize: 11, minWidth: 20, paddingTop: 2 }}>{String(st.n).padStart(2, "0")}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={done ? { textDecoration: "line-through", color: "var(--fg-3)" } : undefined}>{st.title}</b>
                  {st.photo && <span className="chip" style={{ fontSize: 10, marginLeft: 6 }}>photo · {st.photo}</span>}
                  {st.note && <div className="muted" style={{ fontSize: 12 }}>{st.note}</div>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {open && result.concrete && result.concrete.timeText && <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>{result.concrete.timeText}</p>}
    </div>
  );
}

/* the readiness the crew must know before the first hole / the first coat */
function ConcreteReadyChips({ wo, store }: any) {
  if (!wo || verticalOf(wo) !== "concrete") return null;
  const q = quoteOf(wo, store);
  const jts = jobTypesOf(q || wo);
  const r = wo.readiness || {};
  const chip = (ok: boolean, yes: string, no: string) => <span className="chip" style={{ fontSize: 10.5, color: ok ? "var(--green)" : "var(--red)", borderColor: "var(--line-2)" }}>{ok ? yes : no}</span>;
  return (
    <>
      {jts.indexOf("lift") > -1 && chip(!!r.cx_heat, "slab checked ✓", "slab NOT checked for heat / utilities ⚠")}
      {jts.indexOf("coat") > -1 && chip(!!r.cx_moisture, "moisture test ✓", "NO moisture test ⚠")}
      {r.cx_weather ? <span className="chip" style={{ fontSize: 10.5, color: "var(--green)", borderColor: "var(--line-2)" }}>weather ✓</span> : null}
    </>
  );
}

Object.assign(window, { ConcreteCrewSheet, ConcreteStepsCard, ConcreteReadyChips });

export { ConcreteCrewSheet, ConcreteStepsCard, ConcreteReadyChips, cureByJobType, LiftLogCard, LiftLogSummary };


/* ============================================================
   The on-site LIFT LOG (roadmap §13 item 11). Per section the crew lifts:
   how low it sat, where it ended up, the foam that went in and the holes it
   took. Each entry is a field op (job.liftlog) so it queues with no signal
   like a note or a tick; the office reads it on the job card, the customer's
   hub shows the before and after, and the rate cards learn from the foam.
   The sections come from the job's own scope (wo.cx — the drawn slabs or the
   lift blocks), because a crew login has no quote and no rate cards.
   ============================================================ */
const EMPTY = { section: "", sqft: "", beforeIn: "", afterIn: "0", foamLb: "", holes: "", note: "" };
function LiftLogCard({ wo, store, crewId }: any) {
  const isLift = (wo && wo.vertical === "concrete") && (Array.isArray(wo.jobTypes) ? wo.jobTypes.includes("lift") : true);
  const entries: any[] = Array.isArray(wo && wo.liftLog) ? wo.liftLog : [];
  const sections = React.useMemo(() => liftSectionsOf(wo), [wo && wo.cx]);
  const [f, setF] = React.useState<any>(() => ({ ...EMPTY, section: sections[0] ? sections[0].label : "", sqft: sections[0] && sections[0].sqft ? String(sections[0].sqft) : "" }));
  const [open, setOpen] = React.useState<any>(false);
  if (!isLift) return null;
  const set = (p: any) => setF((x: any) => ({ ...x, ...p }));
  const pickSection = (label: string) => { const sec = sections.find((x) => x.label === label); set({ section: label, sqft: sec && sec.sqft ? String(sec.sqft) : f.sqft }); };
  const done = new Set(entries.map((e) => e.section));
  const next = sections.find((sec) => !done.has(sec.label));
  const canAdd = entries.length < LIFT_LOG_MAX && (Number(f.foamLb) > 0 || Number(f.holes) > 0 || Number(f.beforeIn) > 0);
  function add() {
    if (!canAdd) return;
    const at = Date.now();
    const id = "ll-" + at.toString(36) + "-" + Math.floor(Math.random() * 1e4).toString(36);
    const entry: any = { id, section: String(f.section || "").trim() || `Section ${entries.length + 1}`, beforeIn: Number(f.beforeIn) || 0, afterIn: Number(f.afterIn) || 0, foamLb: Number(f.foamLb) || 0, holes: Number(f.holes) || 0, at, crewId: crewId || wo.crewId || undefined };
    if (Number(f.sqft) > 0) entry.sqft = Number(f.sqft);
    if (String(f.note || "").trim()) entry.note = String(f.note).trim();
    store.fieldOp({ kind: "job.liftlog", jobId: wo.id, at, payload: { entry } });
    const after = sections.find((sec) => !done.has(sec.label) && sec.label !== entry.section);
    setF({ ...EMPTY, section: after ? after.label : "", sqft: after && after.sqft ? String(after.sqft) : "" });
    setOpen(false);
  }
  function remove(id: string) { store.fieldOp({ kind: "job.liftlog", jobId: wo.id, at: Date.now(), payload: { removeId: id } }); }
  const t = liftTotals(entries);
  const est = wo.cx && wo.cx.est ? wo.cx.est : null;
  const num = (key: string, label: string, step = 0.25, width = 92) => (
    <div><label className="field-label" htmlFor={"ll-" + key}>{label}</label>
      <input id={"ll-" + key} className="input tnum" type="number" inputMode="decimal" min={0} step={step} style={{ width }} value={f[key]} onChange={(e) => set({ [key]: e.target.value })} /></div>
  );
  return (
    <div className="crew-field" data-testid="lift-log">
      <div className="row between" style={{ marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
        <span className="row gap-8 kicker"><Icon name="activity" size={13} style={{ color: "var(--teal)" }} />Lift log</span>
        <span className="muted" style={{ fontSize: 12 }}>{entries.length ? `${entries.length} section${entries.length === 1 ? "" : "s"} · ${t.foamLb} lb foam · ${t.holes} holes` : (sections.length ? `${sections.length} section${sections.length === 1 ? "" : "s"} to lift` : "Log each section as you lift it")}{est && est.foamLb ? ` · planned ${Math.round(est.foamLb)} lb` : ""}</span>
      </div>
      {entries.map((e) => (
        <div key={e.id} className="cx-lift-row">
          <b style={{ flex: "1 1 120px", minWidth: 0 }}>{e.section}</b>
          <span className="tnum">{e.beforeIn}″ → {e.afterIn}″{liftInches(e) > 0 ? ` (↑${liftInches(e)}″)` : ""}</span>
          <span className="tnum">{e.foamLb} lb · {e.holes} holes</span>
          {e.note && <span className="muted" style={{ flexBasis: "100%", fontSize: 12 }}>{e.note}</span>}
          <button className="btn btn-ghost btn-sm" style={{ padding: "2px 6px" }} onClick={() => remove(e.id)} title="Remove this entry"><Icon name="trash" size={12} /></button>
        </div>
      ))}
      {!open ? (
        <button className="btn btn-outline btn-sm" onClick={() => { if (next) pickSection(next.label); setOpen(true); }} disabled={entries.length >= LIFT_LOG_MAX}><Icon name="plus" size={13} />{entries.length ? "Log another section" : "Log the first section"}{next ? ` · ${next.label}` : ""}</button>
      ) : (
        <div className="cx-lift-form">
          <div><label className="field-label" htmlFor="ll-section">Section</label>
            {sections.length ? (
              <select id="ll-section" className="select" value={sections.some((x) => x.label === f.section) ? f.section : "__other"} onChange={(e) => { if (e.target.value === "__other") set({ section: "" }); else pickSection(e.target.value); }}>
                {sections.map((sec) => <option key={sec.label} value={sec.label}>{sec.label}{done.has(sec.label) ? " ✓" : ""}{sec.sqft ? ` · ${sec.sqft} sq ft` : ""}</option>)}
                <option value="__other">Other…</option>
              </select>
            ) : <input id="ll-section" className="input" placeholder="Driveway by the garage" value={f.section} onChange={(e) => set({ section: e.target.value })} />}
            {sections.length > 0 && !sections.some((x) => x.label === f.section) && <input className="input" style={{ marginTop: 6 }} placeholder="Which section?" value={f.section} onChange={(e) => set({ section: e.target.value })} />}
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            {num("beforeIn", "Low by (in)")}{num("afterIn", "After (in)")}{num("foamLb", "Foam (lb)", 0.5)}{num("holes", "Holes", 1, 80)}{num("sqft", "Area (sq ft)", 1, 100)}
          </div>
          <div><label className="field-label" htmlFor="ll-note">Note</label><input id="ll-note" className="input" placeholder="Voids at the apron, caulked the joint" value={f.note} onChange={(e) => set({ note: e.target.value })} /></div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-sm" disabled={!canAdd} onClick={add}><Icon name="check" size={13} />Save section</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>Saves with no signal like a note. The office sees the foam against the estimate; the customer's page shows the before and after.</p>
    </div>
  );
}

/* the office's read of the lift log on the job card (workorders.tsx FieldFromCrew) */
function LiftLogSummary({ wo }: any) {
  const entries: any[] = Array.isArray(wo && wo.liftLog) ? wo.liftLog : [];
  if (!entries.length) return null;
  const t = liftTotals(entries);
  const rows = liftVariance(wo);
  return (
    <div className="cx-lift-summary">
      <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 13 }}>Lift log · {entries.length} section{entries.length === 1 ? "" : "s"}</b>
        <span className="muted" style={{ fontSize: 12 }}>{t.foamLb} lb foam · {t.holes} holes{t.avgLiftIn ? ` · lifted ${t.avgLiftIn}″ on average` : ""}</span>
      </div>
      {entries.map((e) => <div key={e.id} className="cx-lift-row"><b style={{ flex: "1 1 120px" }}>{e.section}</b><span className="tnum">{e.beforeIn}″ → {e.afterIn}″</span><span className="tnum">{e.foamLb} lb · {e.holes} holes</span>{e.note && <span className="muted" style={{ flexBasis: "100%", fontSize: 12 }}>{e.note}</span>}</div>)}
      {rows.length > 0 && (
        <div className="cx-lift-var">
          {rows.map((r) => <div key={r.label}><span className="muted">{r.label}</span><span className="tnum">{r.actual.toFixed(r.digits)}{r.unit ? " " + r.unit : ""}</span><span className="faint tnum">planned {r.est.toFixed(r.digits)}</span>{r.pct != null && <span className="tnum" style={{ color: Math.abs(r.pct) <= 10 ? "var(--green)" : r.pct > 0 ? "var(--amber)" : "var(--teal)" }}>{r.pct > 0 ? "+" : ""}{r.pct}%</span>}</div>)}
        </div>
      )}
    </div>
  );
}
